import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  Plus,
  Calendar,
  MapPin,
  FileText,
  Upload,
  Download,
  Trash2,
  Edit,
  Eye,
  X,
  Building2,
  CheckCircle2,
  Clock,
  AlertCircle,
  UserPlus,
  Vote,
  PieChart,
  ClipboardList,
  Save,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Altam™ Operating Services
 * AGM Documents Management
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function AGMDocuments() {
  const { user, token } = useAuth();
  const [agmRecords, setAgmRecords] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  
  // Filters
  const [yearFilter, setYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMotionModal, setShowMotionModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedAGM, setSelectedAGM] = useState(null);
  const [quorumStatus, setQuorumStatus] = useState(null);
  const [expandedSections, setExpandedSections] = useState(['details', 'quorum']);
  
  // Form states
  const [form, setForm] = useState({
    year: new Date().getFullYear(),
    agm_date: '',
    agm_time: '19:00',
    location: '',
    notice_date: ''
  });
  
  const [uploadForm, setUploadForm] = useState({
    document_type: 'notice',
    title: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  
  const [motionForm, setMotionForm] = useState({
    motion_number: 1,
    title: '',
    description: '',
    moved_by: '',
    seconded_by: '',
    votes_for: '',
    votes_against: '',
    abstentions: '',
    result: ''
  });
  
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    unit_number: '',
    bio: '',
    elected: false
  });
  
  const [saving, setSaving] = useState(false);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const isResident = user?.role === 'resident';

  const currentYears = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() + 1 - i);

  useEffect(() => {
    if (!isResident) {
      fetchProperties();
    } else {
      fetchAGMRecords();
    }
  }, []);

  useEffect(() => {
    if (selectedProperty || isResident) {
      fetchAGMRecords();
    }
  }, [selectedProperty, currentPage, yearFilter, statusFilter]);

  useEffect(() => {
    if (selectedAGM) {
      fetchQuorumStatus(selectedAGM.id);
    }
  }, [selectedAGM]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/corporations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchAGMRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 10 });
      if (selectedProperty && !isResident) params.append('entity_id', selectedProperty);
      if (yearFilter) params.append('year', yearFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/governance/agm?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgmRecords(response.data.data);
      setDocumentTypes(response.data.document_types || {});
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch AGM records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuorumStatus = async (agmId) => {
    try {
      const response = await axios.get(`${API}/governance/agm/${agmId}/quorum-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuorumStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch quorum status:', error);
    }
  };

  const handleCreateAGM = async () => {
    if (!form.agm_date || !form.location) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        entity_id: selectedProperty,
        year: form.year,
        agm_date: form.agm_date,
        agm_time: form.agm_time,
        location: form.location,
        notice_date: form.notice_date || null,
        status: 'planning'
      };

      await axios.post(`${API}/governance/agm`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('AGM created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchAGMRecords();
    } catch (error) {
      console.error('Failed to create AGM:', error);
      alert('Failed to create AGM. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAGM = async (updates) => {
    if (!selectedAGM) return;

    try {
      await axios.put(`${API}/governance/agm/${selectedAGM.id}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh the selected AGM
      const response = await axios.get(`${API}/governance/agm/${selectedAGM.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAGM(response.data);
      fetchQuorumStatus(selectedAGM.id);
      fetchAGMRecords();
    } catch (error) {
      console.error('Failed to update AGM:', error);
      alert('Failed to update AGM.');
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadForm.title) {
      alert('Please select a file and enter a title');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', uploadForm.document_type);
      formData.append('title', uploadForm.title);

      await axios.post(`${API}/governance/agm/${selectedAGM.id}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Document uploaded!');
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadForm({ document_type: 'notice', title: '' });
      
      // Refresh AGM
      const response = await axios.get(`${API}/governance/agm/${selectedAGM.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAGM(response.data);
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/governance/agm/${selectedAGM.id}/documents/${doc.id}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;

    try {
      await axios.delete(`${API}/governance/agm/${selectedAGM.id}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const response = await axios.get(`${API}/governance/agm/${selectedAGM.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAGM(response.data);
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const handleAddMotion = async () => {
    if (!motionForm.title) {
      alert('Please enter a motion title');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/governance/agm/${selectedAGM.id}/motions`, {
        ...motionForm,
        votes_for: motionForm.votes_for ? parseInt(motionForm.votes_for) : null,
        votes_against: motionForm.votes_against ? parseInt(motionForm.votes_against) : null,
        abstentions: motionForm.abstentions ? parseInt(motionForm.abstentions) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowMotionModal(false);
      setMotionForm({
        motion_number: (selectedAGM.motions?.length || 0) + 2,
        title: '',
        description: '',
        moved_by: '',
        seconded_by: '',
        votes_for: '',
        votes_against: '',
        abstentions: '',
        result: ''
      });
      
      const response = await axios.get(`${API}/governance/agm/${selectedAGM.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAGM(response.data);
    } catch (error) {
      console.error('Failed to add motion:', error);
      alert('Failed to add motion.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCandidate = async () => {
    if (!candidateForm.name) {
      alert('Please enter a candidate name');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/governance/agm/${selectedAGM.id}/candidates`, candidateForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowCandidateModal(false);
      setCandidateForm({ name: '', unit_number: '', bio: '', elected: false });
      
      const response = await axios.get(`${API}/governance/agm/${selectedAGM.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAGM(response.data);
    } catch (error) {
      console.error('Failed to add candidate:', error);
      alert('Failed to add candidate.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleElected = async (candidateId, elected) => {
    try {
      await axios.put(
        `${API}/governance/agm/${selectedAGM.id}/candidates/${candidateId}?elected=${elected}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const response = await axios.get(`${API}/governance/agm/${selectedAGM.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAGM(response.data);
    } catch (error) {
      console.error('Failed to update candidate:', error);
    }
  };

  const handleDeleteAGM = async (agmId) => {
    if (!confirm('Delete this AGM record and all associated documents? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/governance/agm/${agmId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('AGM deleted!');
      setShowViewModal(false);
      fetchAGMRecords();
    } catch (error) {
      console.error('Failed to delete AGM:', error);
      alert('Failed to delete AGM.');
    }
  };

  const openViewModal = async (agm) => {
    try {
      const response = await axios.get(`${API}/governance/agm/${agm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAGM(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.error('Failed to fetch AGM details:', error);
    }
  };

  const resetForm = () => {
    setForm({
      year: new Date().getFullYear(),
      agm_date: '',
      agm_time: '19:00',
      location: '',
      notice_date: ''
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      planning: 'bg-slate-100 text-slate-700',
      notice_sent: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700',
      archived: 'bg-slate-100 text-slate-500'
    };
    const labels = {
      planning: 'Planning',
      notice_sent: 'Notice Sent',
      completed: 'Completed',
      archived: 'Archived'
    };
    return { style: styles[status] || 'bg-slate-100 text-slate-700', label: labels[status] || status };
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6" data-testid="agm-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AGM Documents</h1>
          <p className="text-slate-500">Annual General Meeting records and packages</p>
        </div>
        
        <div className="flex items-center gap-3">
          {!isResident && properties.length > 0 && (
            <select
              value={selectedProperty}
              onChange={(e) => { setSelectedProperty(e.target.value); setCurrentPage(1); }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              {properties.map((prop) => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          )}
          
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New AGM
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={yearFilter}
          onChange={(e) => { setYearFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        >
          <option value="">All Years</option>
          {currentYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        >
          <option value="">All Status</option>
          <option value="planning">Planning</option>
          <option value="notice_sent">Notice Sent</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* AGM Records List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : agmRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-500">
            <Users className="w-12 h-12 text-slate-300 mb-3" />
            <p>No AGM records found</p>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
              >
                Create your first AGM →
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {agmRecords.map((agm) => {
              const status = getStatusBadge(agm.status);
              return (
                <div
                  key={agm.id}
                  className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => openViewModal(agm)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="bg-violet-100 p-2 rounded-lg">
                        <Users className="w-5 h-5 text-violet-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{agm.year} Annual General Meeting</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(agm.agm_date)}
                            {agm.agm_time && ` at ${agm.agm_time}`}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {agm.location}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          <span>{agm.documents?.length || 0} documents</span>
                          <span>{agm.motions?.length || 0} motions</span>
                          <span>{agm.candidates?.length || 0} candidates</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${status.style}`}>
                        {status.label}
                      </span>
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAGM(agm.id); }}
                          className="p-1 text-slate-400 hover:text-rose-600 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {pagination.pages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={10}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Create AGM Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Create New AGM</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Year *</label>
                  <select
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    {currentYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">AGM Date *</label>
                  <input
                    type="date"
                    value={form.agm_date}
                    onChange={(e) => setForm({ ...form, agm_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time</label>
                  <input
                    type="time"
                    value={form.agm_time}
                    onChange={(e) => setForm({ ...form, agm_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notice Date</label>
                  <input
                    type="date"
                    value={form.notice_date}
                    onChange={(e) => setForm({ ...form, notice_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location *</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g., Party Room, 123 Main St"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAGM}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create AGM'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View AGM Modal */}
      {showViewModal && selectedAGM && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{selectedAGM.year} Annual General Meeting</h2>
                <p className="text-sm text-slate-500">{selectedAGM.entity_name}</p>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <select
                    value={selectedAGM.status}
                    onChange={(e) => handleUpdateAGM({ status: e.target.value })}
                    className="px-3 py-1 border border-slate-300 rounded-lg text-sm"
                  >
                    <option value="planning">Planning</option>
                    <option value="notice_sent">Notice Sent</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                )}
                <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Meeting Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-slate-500">Date & Time</p>
                  <p className="font-medium text-slate-900">
                    {formatDate(selectedAGM.agm_date)}
                    {selectedAGM.agm_time && ` at ${selectedAGM.agm_time}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="font-medium text-slate-900">{selectedAGM.location}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Notice Sent</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedAGM.notice_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Units</p>
                  <p className="font-medium text-slate-900">{selectedAGM.total_units}</p>
                </div>
              </div>

              {/* Quorum Status */}
              {quorumStatus && (
                <div className={`rounded-lg p-4 ${quorumStatus.quorum_achieved ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <PieChart className="w-4 h-4" />
                      Quorum Status
                    </h3>
                    {quorumStatus.quorum_achieved ? (
                      <span className="text-emerald-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> Quorum Achieved
                      </span>
                    ) : (
                      <span className="text-amber-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {quorumStatus.units_needed} more needed
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Required ({quorumStatus.quorum_percentage_required}%)</p>
                      <p className="font-semibold text-slate-900">{quorumStatus.quorum_required} units</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Present ({quorumStatus.quorum_percentage_achieved}%)</p>
                      <p className="font-semibold text-slate-900">{quorumStatus.total_present} units</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Breakdown</p>
                      <p className="font-semibold text-slate-900">
                        {quorumStatus.attendees_in_person} in person, {quorumStatus.attendees_by_proxy} proxy
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-4 mt-4 pt-4 border-t border-slate-200">
                      <div className="flex-1">
                        <label className="block text-xs text-slate-500 mb-1">In Person</label>
                        <input
                          type="number"
                          value={selectedAGM.attendees_in_person || ''}
                          onChange={(e) => handleUpdateAGM({ attendees_in_person: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-1 border border-slate-300 rounded-lg text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-slate-500 mb-1">By Proxy</label>
                        <input
                          type="number"
                          value={selectedAGM.attendees_by_proxy || ''}
                          onChange={(e) => handleUpdateAGM({ attendees_by_proxy: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-1 border border-slate-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documents Section */}
              <div className="border border-slate-200 rounded-lg">
                <button
                  onClick={() => toggleSection('documents')}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                >
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documents ({selectedAGM.documents?.length || 0})
                  </h3>
                  {expandedSections.includes('documents') ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedSections.includes('documents') && (
                  <div className="border-t border-slate-200 p-4">
                    {isAdmin && (
                      <button
                        onClick={() => setShowUploadModal(true)}
                        className="mb-4 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-indigo-400 hover:text-indigo-600 w-full flex items-center justify-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Upload Document
                      </button>
                    )}
                    {selectedAGM.documents?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedAGM.documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{doc.title}</p>
                                <p className="text-xs text-slate-500">{doc.document_type_name} • {formatFileSize(doc.file_size)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDownloadDocument(doc)}
                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  className="p-1 text-rose-600 hover:bg-rose-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">No documents uploaded yet</p>
                    )}
                  </div>
                )}
              </div>

              {/* Motions Section */}
              <div className="border border-slate-200 rounded-lg">
                <button
                  onClick={() => toggleSection('motions')}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                >
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Vote className="w-4 h-4" />
                    Motions ({selectedAGM.motions?.length || 0})
                  </h3>
                  {expandedSections.includes('motions') ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedSections.includes('motions') && (
                  <div className="border-t border-slate-200 p-4">
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setMotionForm({ ...motionForm, motion_number: (selectedAGM.motions?.length || 0) + 1 });
                          setShowMotionModal(true);
                        }}
                        className="mb-4 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-indigo-400 hover:text-indigo-600 w-full flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Motion
                      </button>
                    )}
                    {selectedAGM.motions?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedAGM.motions.map((motion, idx) => (
                          <div key={motion.id || idx} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-slate-900">
                                  Motion {motion.motion_number}: {motion.title}
                                </p>
                                {motion.description && (
                                  <p className="text-sm text-slate-600 mt-1">{motion.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                  {motion.moved_by && <span>Moved by: {motion.moved_by}</span>}
                                  {motion.votes_for !== null && (
                                    <span>For: {motion.votes_for} | Against: {motion.votes_against} | Abstain: {motion.abstentions}</span>
                                  )}
                                </div>
                              </div>
                              {motion.result && (
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  motion.result === 'passed' ? 'bg-emerald-100 text-emerald-700' :
                                  motion.result === 'failed' ? 'bg-rose-100 text-rose-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {motion.result}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">No motions recorded yet</p>
                    )}
                  </div>
                )}
              </div>

              {/* Candidates Section */}
              <div className="border border-slate-200 rounded-lg">
                <button
                  onClick={() => toggleSection('candidates')}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                >
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Board Candidates ({selectedAGM.candidates?.length || 0})
                  </h3>
                  {expandedSections.includes('candidates') ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedSections.includes('candidates') && (
                  <div className="border-t border-slate-200 p-4">
                    {isAdmin && (
                      <button
                        onClick={() => setShowCandidateModal(true)}
                        className="mb-4 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-indigo-400 hover:text-indigo-600 w-full flex items-center justify-center gap-2"
                      >
                        <UserPlus className="w-4 h-4" />
                        Add Candidate
                      </button>
                    )}
                    {selectedAGM.candidates?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedAGM.candidates.map((candidate) => (
                          <div key={candidate.id} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{candidate.name}</p>
                                {candidate.unit_number && (
                                  <p className="text-xs text-slate-500">Unit {candidate.unit_number}</p>
                                )}
                                {candidate.bio && (
                                  <p className="text-sm text-slate-600 mt-1">{candidate.bio}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {isAdmin ? (
                                  <button
                                    onClick={() => handleToggleElected(candidate.id, !candidate.elected)}
                                    className={`px-2 py-1 text-xs rounded-full ${
                                      candidate.elected
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                  >
                                    {candidate.elected ? '✓ Elected' : 'Mark Elected'}
                                  </button>
                                ) : candidate.elected && (
                                  <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700">
                                    Elected
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">No candidates registered yet</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Upload AGM Document</h2>
              <button onClick={() => { setShowUploadModal(false); setUploadFile(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Document Type</label>
                <select
                  value={uploadForm.document_type}
                  onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {Object.entries(documentTypes).map(([key, name]) => (
                    <option key={key} value={key}>{name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g., Notice of AGM 2026"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">File *</label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-indigo-400">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="hidden"
                    id="agm-file-upload"
                  />
                  <label htmlFor="agm-file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    {uploadFile ? (
                      <p className="text-sm text-slate-900 font-medium">{uploadFile.name}</p>
                    ) : (
                      <p className="text-sm text-slate-600">Click to select file (PDF, DOC, DOCX)</p>
                    )}
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowUploadModal(false); setUploadFile(null); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadDocument}
                disabled={saving || !uploadFile}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Motion Modal */}
      {showMotionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Add Motion</h2>
              <button onClick={() => setShowMotionModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motion #</label>
                  <input
                    type="number"
                    value={motionForm.motion_number}
                    onChange={(e) => setMotionForm({ ...motionForm, motion_number: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Result</label>
                  <select
                    value={motionForm.result}
                    onChange={(e) => setMotionForm({ ...motionForm, result: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="">Pending</option>
                    <option value="passed">Passed</option>
                    <option value="failed">Failed</option>
                    <option value="tabled">Tabled</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={motionForm.title}
                  onChange={(e) => setMotionForm({ ...motionForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g., Approval of 2026 Budget"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={motionForm.description}
                  onChange={(e) => setMotionForm({ ...motionForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Moved By</label>
                  <input
                    type="text"
                    value={motionForm.moved_by}
                    onChange={(e) => setMotionForm({ ...motionForm, moved_by: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Seconded By</label>
                  <input
                    type="text"
                    value={motionForm.seconded_by}
                    onChange={(e) => setMotionForm({ ...motionForm, seconded_by: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Votes For</label>
                  <input
                    type="number"
                    value={motionForm.votes_for}
                    onChange={(e) => setMotionForm({ ...motionForm, votes_for: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Against</label>
                  <input
                    type="number"
                    value={motionForm.votes_against}
                    onChange={(e) => setMotionForm({ ...motionForm, votes_against: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Abstain</label>
                  <input
                    type="number"
                    value={motionForm.abstentions}
                    onChange={(e) => setMotionForm({ ...motionForm, abstentions: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowMotionModal(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMotion}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add Motion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Candidate Modal */}
      {showCandidateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Add Board Candidate</h2>
              <button onClick={() => setShowCandidateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={candidateForm.name}
                  onChange={(e) => setCandidateForm({ ...candidateForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Unit Number</label>
                <input
                  type="text"
                  value={candidateForm.unit_number}
                  onChange={(e) => setCandidateForm({ ...candidateForm, unit_number: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bio</label>
                <textarea
                  value={candidateForm.bio}
                  onChange={(e) => setCandidateForm({ ...candidateForm, bio: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows={3}
                  placeholder="Brief biography for the information circular..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCandidateModal(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCandidate}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add Candidate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
